import * as React from 'react';
import localeService from 'src/dataServices/LocaleService';

const SIDEBAR_CLOSE_TRANSITION_MS = 200;
const SCROLL_LOCK_CLASS = 'copilot-sidebar-scroll-locked';
const CHAT_INPUT_SELECTOR = '.copilotKitInput textarea';
const CHAT_WINDOW_SELECTOR = '.copilotKitWindow';

interface ICopilotSidebarState {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const setSidebarScrollLock = (locked: boolean): void => {
  document.documentElement.classList.toggle(SCROLL_LOCK_CLASS, locked);
  document.body.classList.toggle(SCROLL_LOCK_CLASS, locked);
};

const labelCopilotTextarea = (): void => {
  const textarea = document.querySelector<HTMLTextAreaElement>(CHAT_INPUT_SELECTOR);
  if (textarea && !textarea.getAttribute('aria-label')) {
    textarea.setAttribute('aria-label', localeService.getLocaleContent().chatInputAriaLabel);
  }
};

export const useCopilotSidebarState = (): ICopilotSidebarState => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    if (sidebarOpen) {
      setSidebarScrollLock(true);
      return undefined;
    }

    // Keep the lock until CopilotKit's slide-out transition completes.
    const timeoutId = window.setTimeout(() => {
      setSidebarScrollLock(false);
    }, SIDEBAR_CLOSE_TRANSITION_MS);

    return () => {
      window.clearTimeout(timeoutId);
      setSidebarScrollLock(false);
    };
  }, [sidebarOpen]);

  React.useEffect(() => {
    return () => setSidebarScrollLock(false);
  }, []);

  React.useEffect(() => {
    if (!sidebarOpen) {
      return undefined;
    }

    labelCopilotTextarea();

    const observer = new MutationObserver(labelCopilotTextarea);
    const targetNode = document.querySelector(CHAT_WINDOW_SELECTOR)?.parentElement ?? document.body;

    observer.observe(targetNode, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [sidebarOpen]);

  return { sidebarOpen, setSidebarOpen };
};